import React, { useEffect, useRef, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import dayjs from "dayjs";

/**
 * One ratio over time (e.g. DAU/MAU) against labeled benchmark lines.
 * Single series, so no legend: the card title names it. Draws at the
 * measured pixel width (no viewBox scaling) so text and strokes stay crisp.
 */

const INK = "#142C5C";
const MUTED = "#6b7280";
const GRID = "#eef0f3";
const REF = "#c4c9d2";
const HEIGHT = 200;
// Right padding only needs to fit the end dot; benchmark labels sit inside the plot.
const PAD = { top: 16, right: 10, bottom: 26, left: 40 };

const pct = (v, digits = 1) =>
  v == null ? "—" : `${(v * 100).toFixed(digits)}%`;

const useWidth = () => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return undefined;
    const ro = new ResizeObserver(([entry]) =>
      setWidth(entry.contentRect.width)
    );
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
};

/**
 * @param {object} props
 * @param {string} props.title e.g. "Daily habit"
 * @param {string} props.metric e.g. "DAU / MAU"
 * @param {Array<{date: string, value: number|null}>} props.points oldest first
 * @param {Array<{value: number, label: string}>} props.benchmarks
 * @param {(v: number) => {label: string, tone: "good"|"ok"|"low"}} [props.verdict]
 * @param {(p: object) => React.ReactNode} [props.tooltip] extra tooltip lines
 */
export default function RatioChart({
  title,
  metric,
  points,
  benchmarks,
  verdict,
  tooltip,
}) {
  const [boxRef, width] = useWidth();
  const [hover, setHover] = useState(null);

  const valid = points.filter((p) => p.value != null);
  const latest = valid[valid.length - 1];
  const avg = valid.length
    ? valid.reduce((a, p) => a + p.value, 0) / valid.length
    : null;
  // Headline: average of the last 7 days, smoother than any single day
  const recent = valid.slice(-7);
  const headline = recent.length
    ? recent.reduce((a, p) => a + p.value, 0) / recent.length
    : null;
  const v = headline != null && verdict ? verdict(headline) : null;

  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const top = Math.max(
    ...benchmarks.map((b) => b.value),
    ...valid.map((p) => p.value),
    0.1
  );
  const yMax = Math.min(1, Math.ceil((top * 1.08) / 0.1) * 0.1);
  const x = (i) =>
    PAD.left +
    (points.length > 1 ? (i / (points.length - 1)) * plotW : plotW / 2);
  const y = (val) => PAD.top + plotH - (val / yMax) * plotH;

  let line = "";
  let area = "";
  points.forEach((p, i) => {
    if (p.value == null) return;
    line += `${line ? "L" : "M"}${x(i).toFixed(1)},${y(p.value).toFixed(1)}`;
  });
  if (valid.length > 1) {
    const firstI = points.indexOf(valid[0]);
    const lastI = points.indexOf(latest);
    area = `${line}L${x(lastI).toFixed(1)},${y(0)}L${x(firstI).toFixed(1)},${y(
      0
    )}Z`;
  }

  const gridTicks = [];
  for (let t = 0; t <= yMax + 1e-9; t += yMax > 0.5 ? 0.2 : 0.1)
    gridTicks.push(Math.round(t * 10) / 10);
  const labelEvery = Math.max(
    1,
    Math.ceil(points.length / Math.max(2, Math.floor(plotW / 70)))
  );

  const onMove = (e) => {
    if (!points.length || plotW <= 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    // The hit area itself starts at PAD.left, so measure from its own edge.
    const rel = (e.clientX - rect.left) / rect.width;
    const i = Math.round(rel * (points.length - 1));
    setHover(Math.min(points.length - 1, Math.max(0, i)));
  };
  const h = hover != null ? points[hover] : null;
  const toneColor = { good: "#1f7a4d", ok: "#8a6d00", low: "#b4432f" };

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: "10px",
        p: "16px 18px 10px",
        minWidth: 0,
      }}
    >
      <Typography sx={{ fontSize: "13px", fontWeight: 600, color: MUTED }}>
        {title} · {metric}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          gap: 1.5,
          flexWrap: "wrap",
          mt: 0.25,
        }}
      >
        <Typography
          sx={{
            fontSize: "32px",
            fontWeight: 700,
            color: INK,
            lineHeight: 1.2,
          }}
        >
          {pct(headline)}
        </Typography>
        {v && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: toneColor[v.tone],
              }}
            />
            <Typography
              sx={{ fontSize: "13px", fontWeight: 600, color: "#333" }}
            >
              {v.label}
            </Typography>
          </Box>
        )}
      </Box>
      <Typography sx={{ fontSize: "12px", color: MUTED, mb: 1 }}>
        {latest
          ? `7-day avg to ${dayjs(latest.date).format("MMM D")}`
          : "No data yet"}
      </Typography>

      <Box ref={boxRef} sx={{ position: "relative", width: "100%" }}>
        {width > 0 && (
          <svg
            width={width}
            height={HEIGHT}
            role="img"
            aria-label={`${metric} per day against benchmarks`}
            style={{ display: "block", overflow: "visible" }}
          >
            {gridTicks.map((t) => (
              <g key={t}>
                <line
                  x1={PAD.left}
                  x2={PAD.left + plotW}
                  y1={y(t)}
                  y2={y(t)}
                  stroke={GRID}
                />
                <text
                  x={PAD.left - 8}
                  y={y(t) + 4}
                  textAnchor="end"
                  fontSize="11"
                  fill={MUTED}
                  style={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {Math.round(t * 100)}%
                </text>
              </g>
            ))}
            {benchmarks.map((b) => (
              <g key={b.value}>
                <line
                  x1={PAD.left}
                  x2={PAD.left + plotW}
                  y1={y(b.value)}
                  y2={y(b.value)}
                  stroke={REF}
                  strokeWidth="1"
                />
                <text
                  x={PAD.left + plotW}
                  y={y(b.value) - 5}
                  textAnchor="end"
                  fontSize="11"
                  fill={MUTED}
                >
                  {b.label}
                </text>
              </g>
            ))}
            {area && <path d={area} fill={INK} opacity="0.08" />}
            <path
              d={line}
              fill="none"
              stroke={INK}
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            {latest && (
              <circle
                cx={x(points.indexOf(latest))}
                cy={y(latest.value)}
                r="4.5"
                fill={INK}
                stroke="#fff"
                strokeWidth="2"
              />
            )}
            {points.map((p, i) =>
              i % labelEvery === 0 ? (
                <text
                  key={p.date}
                  x={x(i)}
                  y={HEIGHT - 6}
                  textAnchor={
                    i === 0 ? "start" : x(i) > width - 40 ? "end" : "middle"
                  }
                  fontSize="11"
                  fill={MUTED}
                >
                  {dayjs(p.date).format("MMM D")}
                </text>
              ) : null
            )}
            {h && h.value != null && (
              <g pointerEvents="none">
                <line
                  x1={x(hover)}
                  x2={x(hover)}
                  y1={PAD.top}
                  y2={PAD.top + plotH}
                  stroke="#9aa3b2"
                  strokeWidth="1"
                />
                <circle
                  cx={x(hover)}
                  cy={y(h.value)}
                  r="4.5"
                  fill={INK}
                  stroke="#fff"
                  strokeWidth="2"
                />
              </g>
            )}
            <rect
              x={PAD.left}
              y={PAD.top}
              width={plotW}
              height={plotH}
              fill="transparent"
              onMouseMove={onMove}
              onMouseLeave={() => setHover(null)}
            />
          </svg>
        )}
        {h && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: x(hover),
              transform:
                hover > points.length / 2
                  ? "translateX(calc(-100% - 10px))"
                  : "translateX(10px)",
              background: "#fff",
              border: "1px solid #e0e0e0",
              boxShadow: "0 4px 14px rgba(20,44,92,0.12)",
              borderRadius: "8px",
              padding: "8px 10px",
              fontSize: "12px",
              lineHeight: 1.55,
              color: "#333",
              pointerEvents: "none",
              whiteSpace: "nowrap",
              zIndex: 2,
            }}
          >
            <Box sx={{ color: MUTED }}>
              {dayjs(h.date).format("ddd, MMM D, YYYY")}
            </Box>
            <Box sx={{ fontWeight: 700, color: INK }}>
              {metric} {pct(h.value)}
            </Box>
            {tooltip && tooltip(h)}
          </Box>
        )}
      </Box>
    </Paper>
  );
}
